import axios from "axios";

// 401s that are an expected answer, not a dead session: the initial /auth/me
// probe, the login endpoints themselves, and the development board (its own
// sign-in expects 401s). The guard's 503 auth_revalidation_unavailable is
// deliberately not intercepted — it surfaces as a regular request error.
const EXCLUDED_URL_PARTS = [
  "/auth/me",
  "/auth/login",
  "/auth/smartid/init",
  "/auth/smartid/status",
  "/api/development",
  // The assistant answers 401 `session_changed` for drift that is NOT a dead
  // session: an entity switch in another tab produces exactly that code while
  // the user stays perfectly signed in (ticket 21, §D1). The widget handles it
  // itself — it clears its memory and asks AuthContext to re-read /auth/me,
  // which routes out only if the session really is gone.
  "/api/assistant",
  // Translation answers 401 `session_changed` for the same kind of drift that
  // is NOT a dead session: an entity switch in another tab produces exactly
  // that code while the user stays signed in (ticket 23, §D3). The form shows
  // the failure beside the button and leaves the text untouched; routing the
  // whole app to /login over a translate click would be absurd.
  "/api/translate",
  // Recognition answers 401 `session_changed` for the same kind of drift that
  // is NOT a dead session: an entity switch in another tab produces exactly
  // that code while the user stays signed in (ticket 24, §D8), and so does the
  // *second* checkpoint, which fires after the document has already been read.
  // The dialog shows the failure beside the file and leaves the form untouched;
  // routing the whole app to /login over it would throw away the person's
  // half-filled entry.
  "/api/recognize",
];

// `/auth/smartid/entities` used to need a special case here: the Header asked
// it on every cabinet mount purely to detect a Smart-ID session, and its 401
// for an admin-impersonation session logged those admins out on every page
// load (docs/TECH_DEBT.md, Ticket 15 §2). Ticket 19 removed the probe —
// `GET /auth/smartid/memberships` answers 200 for a passport session — so
// `/entities` has exactly one caller again, /select-role, where a 401 really
// does mean the session is gone and forcing login is right. `/memberships` is
// deliberately not excluded either, for the same reason: while signed in it
// never answers 401.

// Only our API surface forces a logout. The inclusion check keeps the global
// fetch wrapper from ever reacting to a cross-origin 401/403 — e.g. an expired
// S3 presigned upload URL — which must not log the user out.
function isOurApi(requestUrl: string): boolean {
  return requestUrl.includes("/api/") || requestUrl.includes("/auth/");
}

/**
 * True when a failed request means the session is gone and the user must be
 * routed to /login (ticket 7 §F). Pure — unit-tested as a truth table.
 */
export function shouldForceLogin(
  status: number,
  requestUrl: string,
  currentPath: string
): boolean {
  if (status !== 401) return false;
  if (currentPath === "/login" || currentPath.startsWith("/login/")) return false;
  if (!requestUrl || !isOurApi(requestUrl)) return false;
  return !EXCLUDED_URL_PARTS.some((part) => requestUrl.includes(part));
}

// A voluntary logout destroys the session while the signed-in frame is still on
// screen (AuthContext.logout leaves it there until the full navigation, BUG-42),
// so requests already in flight — polling, the health check, a socket-driven
// refetch — come back 401 before the page unloads. That 401 is the answer the
// user asked for, not an expiry: it must not send them to
// /login?error=session_expired. Module state rather than an argument, because
// the fact belongs to the tab and shouldForceLogin stays a pure truth table.
let loggingOut = false;

/** Raised when a logout starts; lowered only if it fails and the user stays signed in. */
export function setLoggingOut(value: boolean): void {
  loggingOut = value;
}

function isSessionExpiry(status: number, requestUrl: string): boolean {
  if (loggingOut) return false;
  return shouldForceLogin(status, requestUrl, window.location.pathname);
}

function requestUrlOf(input: unknown): string {
  if (typeof input === "string") return input;
  if (input instanceof Request) return input.url;
  if (input instanceof URL) return input.toString();
  return "";
}

/**
 * Install the global 401 handler on both axios and window.fetch, so every API
 * caller — the app uses both — routes a dead session to /login consistently
 * (ticket 7 §F; code review r2 finding 2). Returns an eject function that
 * removes the axios interceptor and restores the original fetch. The
 * response/rejection is always passed through untouched.
 */
export function registerAuthInterceptor(onExpired: () => void): () => void {
  const axiosId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status: number | undefined = error?.response?.status;
      const requestUrl: string = error?.config?.url ?? "";
      if (typeof status === "number" && isSessionExpiry(status, requestUrl)) {
        onExpired();
      }
      return Promise.reject(error);
    }
  );

  const originalFetch = window.fetch.bind(window);
  const patchedFetch: typeof window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      if (isSessionExpiry(response.status, requestUrlOf(args[0]))) {
        onExpired();
      }
    } catch {
      // never let the session check interfere with the caller's response
    }
    return response;
  };
  window.fetch = patchedFetch;

  return () => {
    axios.interceptors.response.eject(axiosId);
    if (window.fetch === patchedFetch) window.fetch = originalFetch;
  };
}
